/**
 * RunStepsLog — the Steps section, whole. The ONE thing either host renders.
 *
 * `DetailedRunLog` is the presentation and stays a pure, fully-props-driven
 * component (it is rendered directly by its own spec, with hand-built rows).
 * This is the container over it: it takes the RUN — a status snapshot and the
 * catalog — and derives everything `DetailedRunLog` needs through
 * `runStepsInputs`, so neither ora nor the plugin holds a line of Steps
 * derivation any more.
 *
 * Why a container and not "just pass the props": the props ARE the bug. ora and
 * the plugin each assembled `chronology` / `manifest` / `currentStepId` /
 * `isFailed` in their own drawer, and the plugin's manifest came from a host
 * prop that is legitimately absent on two of its open paths — so the same run
 * rendered 62 rows with 17 marked never-run in ora and 45 all-green rows in the
 * plugin. Sharing the leaf component was never enough; the inputs had to move
 * too.
 *
 * Import paths:
 *   ora    → ~/common/workflows/drawer/sections/RunStepsLog
 *   plugin → @product-app/common/workflows/drawer/sections/RunStepsLog
 */

import { useMemo, type ReactNode } from "react";
import type { InstanceStatusSnapshot } from "../../types";
import { readChronology } from "../../phaseModel";
import { DetailedRunLog, type RunLogRow } from "./DetailedRunLog";
import {
  buildRunLogManifest,
  resolveRunLogPointer,
  resolveStepCatalogEntry,
  type StepCatalogEntryLike,
} from "./runStepsInputs";

export interface RunStepsLogProps {
  /** The run's own status payload. `null` before the first fetch lands. */
  status: InstanceStatusSnapshot | null | undefined;
  /**
   * The full workflow catalog. The entry is resolved from the RUN's
   * `workflow_id`, which is the only identifier that is always present —
   * a host that opened the drawer from a notification knows the instance and
   * nothing else.
   */
  catalog?: readonly StepCatalogEntryLike[] | null;
  /**
   * An entry the host already holds (the plugin's launch entry, ora's
   * grid-row match). A hint, never a requirement: it is used only when it
   * carries steps, so a stale or step-less entry cannot shadow the catalog.
   */
  catalogEntry?: StepCatalogEntryLike | null;
  /** Host-side workflow-id hints, tried after the run's own `workflow_id`. */
  workflowType?: string | null;
  /** Host-side last-known step id, used only while the run is live. */
  fallbackStepId?: string | null;
  /** Whether the run has reached a terminal status. */
  isTerminal?: boolean;
  /** Forwarded to DrawerSection. */
  defaultExpanded?: boolean;
  /** Forwarded to DrawerSection — localStorage key for the user's own choice. */
  persistKey?: string;
  /** Forwarded to DetailedRunLog — per-row decoration. The run record's
   *  evidence view tags each row with who acted on it. */
  renderRowPrefix?: (row: RunLogRow) => ReactNode;
  /** Forwarded to DetailedRunLog — overrides the "Steps" heading. */
  title?: string;
  /** Forwarded to DetailedRunLog — render the rows with no accordion. */
  bare?: boolean;
}

export function RunStepsLog({
  status,
  catalog,
  catalogEntry,
  workflowType = null,
  fallbackStepId = null,
  isTerminal = false,
  defaultExpanded,
  persistKey,
  renderRowPrefix,
  title,
  bare,
}: RunStepsLogProps) {
  const chronology = useMemo(() => readChronology(status ?? null), [status]);

  const manifest = useMemo(() => {
    // The host's entry wins only when it actually declares steps — otherwise
    // it is indistinguishable from having no entry at all, and the catalog
    // keyed on the run's own workflow id is the better answer.
    const fromHost = buildRunLogManifest(catalogEntry);
    if (fromHost.length > 0) return fromHost;
    return buildRunLogManifest(
      resolveStepCatalogEntry(catalog, status?.workflow_id, workflowType),
    );
  }, [catalogEntry, catalog, status?.workflow_id, workflowType]);

  const pointer = useMemo(
    () => resolveRunLogPointer({ status, isTerminal, fallbackStepId }),
    [status, isTerminal, fallbackStepId],
  );

  return (
    <DetailedRunLog
      chronology={chronology}
      manifest={manifest}
      currentStepId={pointer.currentStepId}
      isTerminal={pointer.isTerminal}
      isFailed={pointer.isFailed}
      defaultExpanded={defaultExpanded}
      persistKey={persistKey}
      renderRowPrefix={renderRowPrefix}
      title={title}
      bare={bare}
    />
  );
}

export default RunStepsLog;
